'use client';

import React from 'react';
import { getCdnUrl } from '@/utils/cdn';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

export const GreenBannerParkEuropa: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="relative w-full flex flex-col items-center justify-center bg-white overflow-visible px-2 sm:px-4 lg:px-20 py-8 sm:py-16">
      
        <div className="w-full max-w-[95%] sm:max-w-[90%] lg:max-w-[1200px] 2xl:max-w-[1500px] relative" ref={ref}>
        {/* Main Green Banner with Background Image */}
        <div 
          className="rounded-[40px] sm:rounded-[80px] lg:rounded-[60px] 2xl:rounded-[100px] p-6 sm:p-8 lg:px-12 lg:py-20 2xl:px-20 2xl:py-32 flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 lg:gap-0 overflow-visible relative shadow-2xl"
          style={{ backgroundColor: '#145B48' }}
        >
          
          {/* Left Content */}
          <motion.div 
            className="flex-1 space-y-6 sm:space-y-8 z-20 text-center lg:text-left relative lg:pl-0 2xl:pl-12"
            style={{ perspective: 1000 }}
            initial={{ opacity: 0, x: -80, rotateY: -25 }}
            animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : { opacity: 0, x: -80, rotateY: -25 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            {/* Park Europa Logo/Brand */}
            <div className="flex flex-col items-center lg:items-start mb-2">
              <img 
                src={getCdnUrl("/images/park-europa/logobranca.webp")}
                alt="Park Europa Logo" 
                className="w-48 sm:w-72 lg:w-[350px] 2xl:w-[480px] h-auto object-contain"
              />
            </div>

            <div className="flex flex-col lg:flex-row items-center lg:items-end gap-4 sm:gap-6 lg:gap-4">
              {/* Apartamentos 100% financiados */}
              <div className="flex flex-col items-center lg:items-start space-y-0">
                <span className="text-white/80 text-base sm:text-lg font-light leading-tight">Apartamentos</span>
                <span className="text-white font-black text-5xl sm:text-6xl lg:text-[4rem] 2xl:text-[5.5rem] leading-none my-1">100%</span>
                <span className="text-white/80 text-sm sm:text-base font-light leading-tight">financiados</span>
              </div>

              {/* CTA Button */}
              <button className="bg-[#E9882D] hover:bg-[#e09620] ml-0 sm:ml-20 text-white font-light text-sm lg:text-base 2xl:text-lg px-4 sm:px-6 py-3 sm:py-4 lg:px-5 lg:py-4 2xl:px-6 2xl:py-5 rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 leading-tight w-full max-w-[240px] sm:max-w-[260px] lg:max-w-none lg:w-[260px] 2xl:w-[320px] lg:-mr-[80px] 2xl:-mr-[140px] relative z-30">
                Junte renda com até 3 familiares ou amigos
              </button>
            </div>
          </motion.div>

          {/* Center: Minha Casa Minha Vida Badge */}
          <div className="relative w-24 h-24 sm:w-32 sm:h-32 lg:w-32 lg:h-32 2xl:w-44 2xl:h-44 flex-shrink-0 z-40 my-2 sm:my-4 lg:my-0 lg:mx-[-20px] 2xl:mx-[-40px] lg:translate-y-4 2xl:translate-y-8 lg:translate-x-16 2xl:translate-x-28">
            <img 
              src={getCdnUrl("/images/common/logo-mcmv-roda.webp")}
              alt="Selo Minha Casa Minha Vida" 
              className="animate-spin-slow w-full h-full" 
            />
            <img 
              src={getCdnUrl("/images/common/logo-mcmv.webp")}
              alt="Logo MCMV" 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 lg:w-12 lg:h-12 2xl:w-16 2xl:h-16 object-contain" 
            />
          </div>

          {/* Right Content: Pricing Info - Animated */}
          <motion.div 
            className="flex-1 flex flex-col items-center lg:items-start gap-1 sm:gap-2 z-10 lg:pl-32 2xl:pl-56"
            initial={{ opacity: 0, x: 100 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            {/* Icon */}
            <div className="flex items-center justify-center mb-0 lg:mb-0">
              <svg className="w-14 h-14 sm:w-20 sm:h-20 mb-4 sm:mb-6 text-white" fill="currentColor" viewBox="3.65 2 56.7 60" xmlns="http://www.w3.org/2000/svg">
                <g>
                    <path d="M17.97 38.139a1 1 0 1 0-1.935.5 12.78 12.78 0 0 0 12.588 9.629c.467 0 .864-.024 1.17-.053a1 1 0 1 0-.185-1.991 11.2 11.2 0 0 1-8.044-2.415 11.04 11.04 0 0 1-3.594-5.67"></path>
                    <path d="M57.38 21.4c.03-.49.05-.97.05-1.47C57.43 10.04 51.75 2 44.77 2h-5.86a10.27 10.27 0 0 0-7.11 3.1 24 24 0 0 0-3.25-.25c-13.73 0-24.9 12.36-24.9 27.56 0 12.89 7.98 23.85 19.03 26.78l.01.01 5.93 1.73.04.02c.4.14.81.25 1.19.35l.17.04a22.7 22.7 0 0 0 5.43.66c13.73 0 24.9-12.36 24.9-27.55a30 30 0 0 0-2.97-13.05M38.91 4c5.87 0 10.65 7.15 10.65 15.93s-4.78 15.94-10.65 15.94-10.66-7.15-10.66-15.94S33.03 4 38.91 4m0 33.87h4.85c-2.1 7.37-8.19 12.41-15.21 12.41-8.82 0-15.99-8.02-15.99-17.87 0-9.11 6.28-16.77 14.25-17.75a25 25 0 0 0-.56 5.27c0 9.89 5.68 17.94 12.66 17.94m-10.36 20.1a21 21 0 0 1-2.5-.16c-11.63-1.4-20.4-12.32-20.4-25.4 0-14.09 10.27-25.56 22.9-25.56.55 0 1.11.03 1.67.07a19.4 19.4 0 0 0-2.85 5.68c-9.48.67-16.81 9.21-16.81 19.81 0 10.96 8.07 19.87 17.99 19.87 8.12 0 15.13-5.92 17.3-14.49a10.3 10.3 0 0 0 5.44-2.51C50.01 48 40.26 57.97 28.55 57.97"></path>
                    <path d="M38.9 27.22a3.143 3.143 0 0 1-3.14-3.139 1 1 0 0 0-2 0 5.145 5.145 0 0 0 4.14 5.038v1.322a1 1 0 1 0 2 0v-1.322a5.141 5.141 0 0 0-1-10.184 3.143 3.143 0 1 1 3.146-3.147 1 1 0 0 0 2 0A5.146 5.146 0 0 0 39.9 10.75V9.428a1 1 0 1 0-2 0v1.322a5.142 5.142 0 0 0 1 10.185 3.143 3.143 0 1 1 0 6.285"></path>
                </g>
              </svg>
            </div>
            
            {/* Parcelas */}
            <div className="text-center lg:text-left space-y-0 relative -mt-2 sm:-mt-4">
              <p className="text-white/80 text-base sm:text-lg font-medium">Parcelas a partir de</p>
              <div className="flex items-baseline gap-1 sm:gap-2 justify-center lg:justify-start">
                <span className="text-white text-2xl sm:text-3xl font-bold">R$</span>
                <span className="text-white font-black text-5xl sm:text-7xl lg:text-[4.5rem] 2xl:text-[6rem] leading-none">799</span>
                <span className="text-white/50 text-xl sm:text-2xl">*</span>
              </div>
            </div>

            {/* Entrada */}
            <div className="flex items-center gap-4 sm:gap-6 justify-center lg:justify-start">
              <div className="flex flex-col items-end lg:items-start">
                <p className="text-white/80 text-lg sm:text-xl font-light leading-tight text-right lg:text-left">Entrada<br/>parcelada<br/>em até</p>
                <p className="text-white/40 text-[10px] sm:text-[12px] mt-1">*sujeito a análise de crédito</p>
              </div>
              <div className="flex flex-col items-start">
                 <span className="text-[#E9882D] font-black text-5xl sm:text-6xl lg:text-[4.5rem] 2xl:text-[6rem] leading-none ml-1 sm:ml-2 lg:ml-2 2xl:ml-4">36x</span>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Small Green Diamond Filled - Above Orange */}
        <svg 
          className="absolute bottom-36 right-10 sm:bottom-48 sm:right-16 lg:bottom-32 lg:right-2 w-24 h-24 sm:w-32 sm:h-32 lg:w-44 lg:h-44 z-20"
          viewBox="0 0 100 35" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M94.82 18.9 83.46 30.26c-1.72 1.72-4.52 1.72-6.25 0L65.85 18.9a4.41 4.41 0 0 1 0-6.25L77.21 1.29a4.43 4.43 0 0 1 6.25 0l11.36 11.36a4.43 4.43 0 0 1 0 6.25" 
            fill="#289949"
          />
        </svg>

        {/* Green Diamond Outline - Right of Filled */}
        <svg 
          className="absolute bottom-40 -right-12 sm:bottom-52 sm:-right-8 lg:-bottom-20 lg:-right-56 w-48 h-48 sm:w-72 sm:h-72 lg:w-[560px] lg:h-[560px] z-20"
          viewBox="0 0 180 60" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="m170.31 42.88-21.96 21.96a8.534 8.534 0 0 1-12.08 0l-21.96-21.96a8.534 8.534 0 0 1 0-12.08l21.96-21.96a8.55 8.55 0 0 1 12.08 0l21.96 21.96a8.534 8.534 0 0 1 0 12.08Z" 
            fill="none" 
            strokeMiterlimit="10" 
            strokeWidth="1.17" 
            stroke="#289949"
          />
        </svg>

        {/* Decorative Orange Diamond - Bottom Right */}
        <svg 
          className="absolute -bottom-10 -right-12 sm:-bottom-12 sm:-right-14 lg:-bottom-16 lg:-right-28 w-40 h-40 sm:w-56 sm:h-56 lg:w-72 lg:h-72 z-10"
          viewBox="0 0 145 200" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="m138.09 134.48-51.61 51.61c-7.84 7.84-20.56 7.84-28.4 0L6.47 134.48c-7.84-7.84-7.84-20.56 0-28.4l51.61-51.61c7.84-7.84 20.56-7.84 28.4 0l51.61 51.61c7.84 7.84 7.84 20.56 0 28.4Z" 
            stroke="#e9882d" 
            fill="none" 
            strokeMiterlimit="10" 
            strokeWidth="1.17"
          />
        </svg>

        {/* Decorative Elements - Outside the banner */}
      </div>

    </section>
  );
};
